import { useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";
import { http, errMsg } from "../../../lib/api";
import { media } from "../ExtrasShell";

const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X", "XI", "XII", "XIII", "XIV", "XV", "XVI", "XVII", "XVIII", "XIX", "XX", "XXI", "XXII", "XXIII", "XXIV"];
const input = "w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#8B6B4A]";
const label = (k) => k.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
const lines = (a) => (a || []).join("\n");
const unlines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);

export const FieldsForm = ({ value, onSave, testid, extra }) => {
  const [v, setV] = useState(value || {});
  const [busy, setBusy] = useState(false);
  const save = async () => { setBusy(true); await onSave(v); setBusy(false); };
  return (
    <div data-testid={testid} className="space-y-4">
      {Object.entries(v).map(([k, x]) => (
        <label key={k} className="block">
          <div className="ex-eyebrow mb-1">{label(k)}{Array.isArray(x) ? " · one per line" : ""}</div>
          {typeof x === "number" ? <input data-testid={`${testid}-${k}`} type="number" step="0.05" value={x} onChange={(e) => setV({ ...v, [k]: Number(e.target.value) })} className={input} />
            : Array.isArray(x) ? <textarea data-testid={`${testid}-${k}`} rows={Math.min(8, x.length + 1)} value={lines(x)} onChange={(e) => setV({ ...v, [k]: e.target.value.split("\n") })} className={input} />
              : typeof x === "string" ? (x.length > 90 ? <textarea data-testid={`${testid}-${k}`} rows={5} value={x} onChange={(e) => setV({ ...v, [k]: e.target.value })} className={input} />
                : <input data-testid={`${testid}-${k}`} value={x} onChange={(e) => setV({ ...v, [k]: e.target.value })} className={input} />) : null}
        </label>
      ))}
      <div className="flex gap-3">
        <button data-testid={`${testid}-save`} onClick={save} disabled={busy} className="ex-btn ex-btn-dark">{busy && <Loader2 size={14} className="animate-spin" />} Save</button>
        {extra}
      </div>
    </div>
  );
};

const tidy = (o) => Object.fromEntries(Object.entries(o).map(([k, x]) => [k, Array.isArray(x) && x.every((y) => typeof y === "string") ? x.map((y) => y.trim()).filter(Boolean) : x]));

export const usePatch = (p, setP) => async (body) => {
  try {
    const clean = Object.fromEntries(Object.entries(body).map(([k, v]) => [k, v && !Array.isArray(v) && typeof v === "object" ? tidy(v) : v]));
    const { data } = await http.patch(`/extras/products/${p.id}`, clean); setP(data); toast.success("Saved");
  } catch (e) { toast.error(errMsg(e)); }
};

export const RegenButton = ({ p, part, reload, testid }) => (
  <button data-testid={testid} disabled={!!p.busy} onClick={async () => { try { await http.post(`/extras/products/${p.id}/redo`, { part }); toast.success("Rewriting…"); reload(); } catch (e) { toast.error(errMsg(e)); } }}
    className="ex-btn ex-btn-line disabled:opacity-40">{p.busy === part ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Rewrite with AI</button>
);

const SectionView = ({ s }) => (
  <div>
    {s.heading && <h3 className="ex-display text-2xl mb-3">{s.heading}</h3>}
    {s.text && <p className={`text-sm leading-relaxed mb-3 ${s.kind === "warning" ? "bg-[#FBF1E6] border-l-2 border-[#B4743A] p-3" : ""}`}>{s.text}</p>}
    {s.items?.length > 0 && <ul className={`text-sm space-y-1.5 ${s.kind === "warning" ? "bg-[#FBF1E6] border-l-2 border-[#B4743A] p-3" : ""}`}>{s.items.map((x, i) => <li key={i}>{s.kind === "checklist" ? "☐ " : s.kind === "planner" ? "" : "• "}{x}</li>)}</ul>}
    {s.cards?.length > 0 && <div className="grid md:grid-cols-2 gap-3">{s.cards.map((c, i) => <div key={i} className="ex-card p-3 text-sm">{c.image && <img src={media(c.image)} alt="" className="float-right w-12 h-12 object-contain ml-2" />}<div className="ex-display text-base">{c.name}</div>{c.meta && <div className="ex-mono text-[10px] text-[#8B6B4A] my-1">{c.meta}</div>}<div>{c.text}</div></div>)}</div>}
    {s.steps?.length > 0 && <div className="divide-y divide-dotted divide-stone-300">{s.steps.map((t, i) => <div key={i} className="flex gap-4 py-2 text-sm"><div className="ex-mono text-[11px] text-[#8B6B4A] w-36 shrink-0">{t.time}</div><div>{t.activity}{t.details && <div className="text-stone-500 text-xs mt-0.5">{t.details}</div>}</div></div>)}</div>}
  </div>
);

const SectionEdit = ({ s, onChange }) => (
  <div className="space-y-3">
    <input data-testid="ex-sec-heading" value={s.heading} onChange={(e) => onChange({ ...s, heading: e.target.value })} className={`${input} ex-display text-lg`} />
    <textarea data-testid="ex-sec-text" rows={3} value={s.text} onChange={(e) => onChange({ ...s, text: e.target.value })} placeholder="Intro paragraph" className={input} />
    {(s.items?.length > 0 || !["cards", "timeline"].includes(s.kind)) && <textarea data-testid="ex-sec-items" rows={8} value={lines(s.items)} onChange={(e) => onChange({ ...s, items: e.target.value.split("\n") })} placeholder="One point per line" className={input} />}
    {s.cards?.length > 0 && <textarea data-testid="ex-sec-cards" rows={8} value={s.cards.map((c) => [c.name, c.meta, c.text].join(" | ")).join("\n")} onChange={(e) => onChange({ ...s, cards: unlines(e.target.value).map((l) => { const [name, meta, ...t] = l.split(" | "); const old = s.cards.find((c) => c.name === name); return { ...(old?.image ? { image: old.image } : {}), name, meta: meta || "", text: t.join(" | ") }; }) })} className={input} />}
    {s.steps?.length > 0 && <textarea data-testid="ex-sec-steps" rows={8} value={s.steps.map((c) => [c.time, c.activity, c.details].join(" | ")).join("\n")} onChange={(e) => onChange({ ...s, steps: unlines(e.target.value).map((l) => { const [time, activity, ...d] = l.split(" | "); return { time, activity: activity || "", details: d.join(" | ") }; }) })} className={input} />}
    {(s.cards?.length > 0 || s.steps?.length > 0) && <div className="text-xs text-stone-500">Cards and timeline rows: one per line, parts separated by " | ".</div>}
  </div>
);

const Overview = ({ c }) => (
  <div>
    {c.overview && <p className="ex-display italic text-xl leading-relaxed text-stone-700 mb-6">{c.overview}</p>}
    <div className="grid md:grid-cols-3 gap-3">{(c.quick_facts || []).map((f, i) => (
      <div key={i} className="border border-[#E6DDCF] bg-white p-3 text-sm"><div className="ex-mono text-[9px] tracking-[0.18em] uppercase text-stone-500 mb-1">{f.label}</div>{f.value}</div>
    ))}</div>
  </div>
);

export const ContentsTab = ({ p, save, editing, setEditing }) => {
  const c = p.content;
  const [i, setI] = useState(0);
  const [edit, setEdit] = useState(null);
  if (!c?.sections?.length) return <div className="text-sm text-stone-500">No contents yet.</div>;
  const all = [{ heading: "Overview", overview: true }, ...c.sections];
  const s = all[i];
  const open = editing && !s.overview ? edit || s : null;
  const go = (j) => { setI(j); setEdit(null); setEditing(false); };
  const commit = async () => {
    const sections = c.sections.map((x, j) => (j === i - 1 ? { ...open, items: (open.items || []).map((y) => y.trim()).filter(Boolean) } : x));
    await save({ content: { ...c, sections } }); setEdit(null); setEditing(false);
  };
  return (
    <div data-testid="ex-contents" className="grid md:grid-cols-[210px_1fr] gap-6">
      <div className="space-y-0.5">
        {all.map((x, j) => (
          <button key={j} data-testid={`ex-sec-nav-${j}`} onClick={() => go(j)} className={`w-full flex gap-3 text-left text-[13px] px-3 py-2 transition-colors ${i === j ? "bg-[#30271D] text-white" : "text-stone-700 hover:bg-[#F1ECE4]"}`}>
            <span className={`ex-display text-[11px] w-8 shrink-0 ${i === j ? "text-[#C9A847]" : "text-stone-400"}`}>{ROMAN[j] || j + 1}.</span><span className="truncate">{x.heading}</span>
          </button>
        ))}
      </div>
      <div className="min-w-0">
        <div className="bg-[#30271D] text-white px-7 py-8 mb-6">
          <div className="ex-display text-3xl text-[#C9A847]">{ROMAN[i] || i + 1}</div>
          <h3 className="ex-display text-3xl mt-2">{s.heading}</h3>
          {s.overview && c.subtitle && <p className="ex-display italic text-white/75 mt-2">{c.subtitle}</p>}
        </div>
        {s.overview ? <Overview c={c} /> : open ? <>
          <SectionEdit s={open} onChange={setEdit} />
          <div className="flex gap-2 mt-4"><button data-testid="ex-sec-save" onClick={commit} className="ex-btn ex-btn-dark">Save section</button><button data-testid="ex-sec-cancel" onClick={() => { setEdit(null); setEditing(false); }} className="ex-btn ex-btn-line">Cancel</button></div>
        </> : <SectionView s={{ ...s, heading: "" }} />}
        {editing && s.overview && <div className="text-xs text-stone-500 mt-4">Pick a section on the left to edit it.</div>}
        <div className="flex justify-between mt-8 text-sm">
          <button data-testid="ex-sec-prev" disabled={i === 0} onClick={() => go(i - 1)} className="text-stone-500 hover:text-stone-900 disabled:opacity-30">← Previous</button>
          <button data-testid="ex-sec-next" disabled={i === all.length - 1} onClick={() => go(i + 1)} className="text-stone-500 hover:text-stone-900 disabled:opacity-30">Next →</button>
        </div>
      </div>
    </div>
  );
};

export const SlidesForm = ({ p, save, reload }) => {
  const [slides, setSlides] = useState(p.carousel?.slides || []);
  const set = (i, k, v) => setSlides(slides.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  return (
    <div data-testid="ex-slides-form" className="space-y-5">
      {slides.map((s, i) => (
        <div key={i} className="ex-card p-4 grid md:grid-cols-[60px_1fr] gap-3">
          <div className="ex-mono text-xs text-stone-500 pt-2">{i + 1}/{slides.length}</div>
          <div className="space-y-2">
            {["kicker", "headline", "body"].map((k) => k === "body"
              ? <textarea key={k} data-testid={`ex-slide-${i}-${k}`} rows={3} value={s[k] || ""} onChange={(e) => set(i, k, e.target.value)} className={input} />
              : <input key={k} data-testid={`ex-slide-${i}-${k}`} value={s[k] || ""} onChange={(e) => set(i, k, e.target.value)} placeholder={k} className={input} />)}
          </div>
        </div>
      ))}
      <div className="flex gap-3">
        <button data-testid="ex-slides-save" onClick={() => save({ carousel: { ...p.carousel, slides } })} className="ex-btn ex-btn-dark">Save slides</button>
        <RegenButton p={p} part="carousel" reload={reload} testid="ex-slides-regen" />
      </div>
    </div>
  );
};
